import React, { useState, useEffect } from 'react';
import { Building, Phone, MapPin, Globe, Mail, Edit2, Calendar } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getOrganizationProfile, createOrganizationProfile, updateOrganizationProfile } from '../../lib/supabase/organization';
import type { OrganizationProfile } from '../../lib/supabase/organization';

export default function CompanyProfile() {
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    domain: '',
    email: '',
    date_founded: '',
    type: 'new' as const
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const profile = await getOrganizationProfile();
      console.log('Loaded profile:', profile);
      setProfile(profile);
      if (profile) {
        setFormData({
          name: profile.name || '',
          phone: profile.phone || '',
          address: profile.address || '',
          domain: profile.domain || '',
          email: profile.email || '',
          date_founded: profile.date_founded ? formatDateForInput(profile.date_founded) : '',
          type: profile.type as 'new' | 'existing'
        });
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string): string => {
    try {
      // Parse the date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) return 'Not set';
    
    // If the string is already in MM/DD/YYYY format, return it
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    try {
      // Parse the date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Not set';
      
      // Format date as MM/DD/YYYY to ensure consistent display
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${month}/${day}/${year}`;
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Not set';
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!user) {
      setError('Authentication error. Please try signing in again.');
      setLoading(false);
      return;
    }
    
    if (!formData.name.trim()) {
      setError('Organization name is required');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      if (profile) {
        // Update existing profile
        await updateOrganizationProfile(profile.id, {
          name: formData.name.trim(),
          phone: formData.phone.trim() || null,
          address: formData.address.trim() || null,
          domain: formData.domain.trim() || null,
          email: formData.email.trim() || null,
          date_founded: formData.date_founded || null
        });
      } else {
        // Create new profile
        await createOrganizationProfile({
          name: formData.name.trim(),
          phone: formData.phone.trim() || null,
          address: formData.address.trim() || null,
          domain: formData.domain.trim() || null,
          email: formData.email.trim() || null,
          date_founded: formData.date_founded || null,
          type: 'new'
        });
      }
      
      await loadProfile();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save organization profile:', error instanceof Error ? error.message : error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to save organization profile');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!profile && !isEditing) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="text-center max-w-md mx-auto">
          <Building className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Tell Us About Your Business</h2>
          <p className="text-gray-600 mb-6">Let's get to know your business better</p>
          <button
            onClick={() => setIsEditing(true)}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Add Your Business Info
          </button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <h2 className="text-xl font-semibold mb-6">
          {profile ? 'Edit Company Profile' : 'Create Company Profile'}
        </h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleFormSubmit} className="max-w-2xl space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter your company name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="contact@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Founded
            </label>
            <div className="relative">
              <input
                type="date"
                value={formData.date_founded}
                onChange={(e) => setFormData(prev => ({ ...prev, date_founded: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                data-testid="date-founded-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Building className="w-6 h-6 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold">Your Business Info</h2>
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center space-x-2 text-blue-500 hover:text-blue-600 sm:self-start"
        >
          <Edit2 className="w-4 h-4" />
          <span>Update Info</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
          <h3 className="font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Building className="w-4 h-4 mr-2" />
                Company Name
              </label>
              <p className="mt-1 text-gray-900">{profile.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                Domain
              </label>
              <p className="mt-1 text-gray-900">{profile.domain || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </label>
              <p className="mt-1 text-gray-900">{profile.email || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                Phone Number
              </label>
              <p className="mt-1 text-gray-900">{profile.phone || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                Address
              </label>
              <p className="mt-1 text-gray-900 whitespace-pre-line">
                {profile.address || 'Not set'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
          <h3 className="font-medium text-gray-900 mb-4">Additional Details</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Date Founded
              </label>
              <p className="mt-1 text-gray-900" data-testid="date-founded-display" id="date-founded-display">
                {formatDateForDisplay(profile.date_founded)}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Member Since
              </label>
              <p className="mt-1 text-gray-900" data-testid="member-since-display">
                {formatDateForDisplay(profile.created_at)}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Last Updated
              </label>
              <p className="mt-1 text-gray-900" data-testid="last-updated-display">
                {formatDateForDisplay(profile.updated_at)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}